import { GraphCalendarEvent, OutlookMeeting } from './outlook-import.types';

/**
 * Graph returns `dateTime` without a trailing `Z` even when the timezone is UTC
 * (we send `Prefer: outlook.timezone="UTC"`). Normalize to a real UTC ISO string
 * so downstream `new Date(...)` / formatters interpret it correctly.
 */
const toUtcIso = (value?: string): string => {
  if (!value) return '';
  const trimmed = value.trim();
  if (trimmed.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(trimmed)) {
    return trimmed;
  }
  return `${trimmed}Z`;
};

export const mapGraphEventToOutlookMeeting = (event: GraphCalendarEvent): OutlookMeeting => {
  const startIso = toUtcIso(event.start?.dateTime);
  const endIso = toUtcIso(event.end?.dateTime);
  const startMs = startIso ? new Date(startIso).getTime() : 0;
  const endMs = endIso ? new Date(endIso).getTime() : 0;
  const durationMinutes = startMs && endMs ? Math.round((endMs - startMs) / 60000) : 0;

  const organizerEmail = event.organizer?.emailAddress?.address ?? '';
  const organizerName = event.organizer?.emailAddress?.name ?? organizerEmail;

  const attendeeEmails = (event.attendees ?? [])
    .map((a) => a.emailAddress?.address ?? '')
    .filter((email): email is string => email.length > 0);

  const isRecurring =
    event.type === 'occurrence' ||
    event.type === 'exception' ||
    event.type === 'seriesMaster' ||
    Boolean(event.seriesMasterId);

  return {
    id: event.id,
    iCalUId: event.iCalUId,
    seriesMasterId: event.seriesMasterId,
    title: event.subject?.trim() || '(No title)',
    startIso,
    endIso,
    startMs,
    durationMinutes,
    organizerName,
    organizerEmail,
    attendeeCount: attendeeEmails.length,
    attendeeEmails,
    isRecurring,
    isOnlineMeeting: Boolean(event.isOnlineMeeting),
    joinUrl: event.onlineMeeting?.joinUrl,
    isCancelled: Boolean(event.isCancelled),
  };
};
